import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PencilIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  fetchClaimantNameDuplicate,
  fetchClaimNumberDuplicate,
  showDuplicateAlert,
  requestDuplicateConfirm,
  runClaimantNameDuplicateCheck
} from '../utils/duplicateClaimChecks';
import DuplicateWarningModal from '../components/DuplicateWarningModal';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';
import { capitalizeName } from '../utils/nameUtils';
import SearchableSelect from '../components/SearchableSelect';

const Claims = () => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateClaimModal, setShowCreateClaimModal] = useState(false);
  const [isSavingClaim, setIsSavingClaim] = useState(false);
  const [claimants, setClaimants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [showClaimantModal, setShowClaimantModal] = useState(false);
  const [showNewClaimantButton, setShowNewClaimantButton] = useState(false);
  const [pendingClaimantId, setPendingClaimantId] = useState(null);
  const [claimantSearchTerm, setClaimantSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('my'); // 'my' | 'all'
  const [allClaimsSearchResults, setAllClaimsSearchResults] = useState([]);
  const [editingClaim, setEditingClaim] = useState(null);
  const [isEditingClaim, setIsEditingClaim] = useState(false);
  const {
    viewClaim,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  const [claimFormData, setClaimFormData] = useState({
    claimant_id: '',
    case_type: 'Workers Compensation',
    claim_number: '',
    date_of_injury: '',
    ct_start_date: '',
    ct_end_date: '',
    diagnosis: '',
    contact_claims_handler_id: '',
    adjusters_assistant_id: '',
    nurse_case_manager_id: '',
    field_case_manager_id: '',
    employer_insured: ''
  });
  const [claimNumberWarning, setClaimNumberWarning] = useState(null);
  const [claimantNameWarning, setClaimantNameWarning] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [claimantFormData, setClaimantFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    phone: '',
    language: '',
    customLanguage: '',
    billing_account_id: '',
    address: '',
    address_latitude: '',
    address_longitude: ''
  });
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const claimantAddressAutocompleteRef = useRef(null);
  const claimantNamesRef = useRef({ first_name: '', last_name: '' });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load claims when page, debounced search, or active tab changes
  useEffect(() => {
    loadClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, activeTab, billingAccounts]);

  // Reset to page 1 when search or tab changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, activeTab]);

  const loadClaims = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 30,
        ...(debouncedSearchTerm && { claim_number: debouncedSearchTerm })
      });
      
      // Only "All Claims" uses billing-account scope. "My Claims" is always handler/assistant only.
      if (activeTab === 'all' && billingAccounts.length > 0) {
        queryParams.append('billingAccountId', billingAccounts[0].id);
      }
      
      const data = await makeAuthenticatedRequest(`/claims?${queryParams}`);

      let myClaims = [];
      if (data && data.success) {
        myClaims = data.data || [];
        setClaims(myClaims);
        if (data.pagination) {
          setTotalCount(data.pagination.total || 0);
        } else {
          setTotalCount(myClaims.length || 0);
        }
      } else {
        toast.error(data?.message || 'Failed to load claims');
        setClaims([]);
        setTotalCount(0);
      }

      // On My Claims + search: also check All Claims for matches not in My Claims
      if (
        activeTab === 'my' &&
        debouncedSearchTerm &&
        billingAccounts.length > 0
      ) {
        const allParams = new URLSearchParams({
          page: 1,
          limit: 30,
          claim_number: debouncedSearchTerm,
          billingAccountId: billingAccounts[0].id
        });
        const allData = await makeAuthenticatedRequest(`/claims?${allParams.toString()}`);
        if (allData?.success) {
          const myIds = new Set(myClaims.map((c) => c.id));
          setAllClaimsSearchResults((allData.data || []).filter((c) => !myIds.has(c.id)));
        } else {
          setAllClaimsSearchResults([]);
        }
      } else {
        setAllClaimsSearchResults([]);
      }
    } catch (error) {
      console.error('Error loading claims:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load claims'));
      setAllClaimsSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClaim = (claim) => {
    viewClaim(claim);
  };

  const handleEditClaim = (claim) => {
    setEditingClaim(claim);
    setClaimFormData({
      claimant_id: claim.claimant_id ? claim.claimant_id.toString() : '',
      case_type: claim.case_type || 'Workers Compensation',
      claim_number: claim.claim_number || '',
      date_of_injury: claim.date_of_injury ? formatDateForInput(claim.date_of_injury) : '',
      ct_start_date: claim.ct_start_date ? formatDateForInput(claim.ct_start_date) : '',
      ct_end_date: claim.ct_end_date ? formatDateForInput(claim.ct_end_date) : '',
      diagnosis: claim.diagnosis || '',
      contact_claims_handler_id: claim.contact_claims_handler_id ? claim.contact_claims_handler_id.toString() : '',
      adjusters_assistant_id: claim.adjusters_assistant_id ? claim.adjusters_assistant_id.toString() : '',
      nurse_case_manager_id: claim.nurse_case_manager_id ? claim.nurse_case_manager_id.toString() : '',
      field_case_manager_id: claim.field_case_manager_id ? claim.field_case_manager_id.toString() : '',
      employer_insured: claim.employer_insured || ''
    });
    setClaimNumberWarning(null);
    setIsEditingClaim(true);
    setShowCreateClaimModal(true);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const handleUpdateClaim = async (e) => {
    e.preventDefault();
    
    if (!claimFormData.claim_number || !claimFormData.case_type) {
      toast.error('Claim number and case type are required');
      return;
    }

    setIsSavingClaim(true);
    try {
      const claimDup = await fetchClaimNumberDuplicate(
        makeAuthenticatedRequest,
        claimFormData.claim_number,
        editingClaim?.id
      );
      setClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        setIsSavingClaim(false);
        return;
      }

      const payload = {
        ...claimFormData,
        claimant_id: claimFormData.claimant_id ? parseInt(claimFormData.claimant_id, 10) : null,
        contact_claims_handler_id: claimFormData.contact_claims_handler_id || null,
        adjusters_assistant_id: claimFormData.adjusters_assistant_id || null,
        nurse_case_manager_id: claimFormData.nurse_case_manager_id || null,
        field_case_manager_id: claimFormData.field_case_manager_id || null,
        date_of_injury: claimFormData.date_of_injury || null,
        ct_start_date: claimFormData.ct_start_date || null,
        ct_end_date: claimFormData.ct_end_date || null,
        diagnosis: claimFormData.diagnosis || null,
        employer_insured: claimFormData.employer_insured || null
      };

      const response = await makeAuthenticatedRequest(`/claims/${editingClaim.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (!response.success) {
        toast.error(response.message || 'Failed to update claim');
        setIsSavingClaim(false);
        return;
      }

      toast.success('Claim updated successfully');
      setShowCreateClaimModal(false);
      setIsEditingClaim(false);
      setEditingClaim(null);
      setIsSavingClaim(false);
      setClaimFormData({
        claimant_id: '',
        case_type: 'Workers Compensation',
        claim_number: '',
        date_of_injury: '',
        ct_start_date: '',
        ct_end_date: '',
        diagnosis: '',
        contact_claims_handler_id: '',
        adjusters_assistant_id: '',
        nurse_case_manager_id: '',
        field_case_manager_id: '',
        employer_insured: ''
      });
      setClaimNumberWarning(null);
      loadClaims();
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to update claim'));
      setIsSavingClaim(false);
    }
  };

  const loadClaimants = async () => {
    try {
      const data = await makeAuthenticatedRequest('/claimants?limit=1000');
      if (data.success) {
        setClaimants(data.data || []);
        return data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading claimants:', error);
      return [];
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await makeAuthenticatedRequest('/customers');
      if (data.success) {
        setCustomers(data.data || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadBillingAccounts = async () => {
    try {
      const data = await makeAuthenticatedRequest('/billing-accounts');
      if (data.success) {
        setBillingAccounts(data.data || []);
      }
    } catch (error) {
      console.error('Error loading billing accounts:', error);
    }
  };

  const loadLanguages = async () => {
    try {
      const data = await makeAuthenticatedRequest('/parametric/languages');
      if (data.success) {
        setLanguages(data.data || []);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  useEffect(() => {
    loadClaimants();
    loadCustomers();
    loadBillingAccounts();
    loadLanguages();
    initializeGoogleMaps();
  }, []);

  const initializeGoogleMaps = () => {
    if (window.google && window.google.maps) {
      setMapsInitialized(true);
    } else {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.warn('Google Maps API key not found. Address autocomplete will be disabled.');
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapsInitialized(true);
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      document.head.appendChild(script);
    }
  };

  const setupClaimantAddressAutocomplete = (inputElement) => {
    if (!inputElement) return;
    
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    // Prevent multiple initializations
    if (claimantAddressAutocompleteRef.current) {
      return;
    }

    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setClaimantFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          address_latitude: lat.toString(),
          address_longitude: lng.toString()
        }));
      }
    });

    claimantAddressAutocompleteRef.current = autocompleteInstance;
  };

  // Auto-set billing account when billing accounts are loaded
  useEffect(() => {
    if (billingAccounts.length > 0 && !claimantFormData.billing_account_id) {
      // Set the first billing account (customer's account) as default
      setClaimantFormData(prev => ({
        ...prev,
        billing_account_id: billingAccounts[0].id.toString()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAccounts]);

  // Auto-select pending claimant when it appears in the list
  useEffect(() => {
    if (pendingClaimantId && claimants.length > 0) {
      const claimantExists = claimants.some(c => c.id.toString() === pendingClaimantId);
      if (claimantExists) {
        setClaimFormData(prev => ({
          ...prev,
          claimant_id: pendingClaimantId
        }));
        setPendingClaimantId(null);
      }
    }
  }, [claimants, pendingClaimantId]);

  const handleCreateClaimClick = () => {
    setEditingClaim(null);
    setIsEditingClaim(false);
    setClaimFormData({
      claimant_id: '',
      case_type: 'Workers Compensation',
      claim_number: '',
      date_of_injury: '',
      ct_start_date: '',
      ct_end_date: '',
      diagnosis: '',
      contact_claims_handler_id: '',
      adjusters_assistant_id: '',
      nurse_case_manager_id: '',
      field_case_manager_id: '',
      employer_insured: ''
    });
    setClaimNumberWarning(null);
    setShowNewClaimantButton(false);
    setShowCreateClaimModal(true);
  };

  const resetClaimantForm = () => {
    // Get the customer's billing account (first one from the list, which should be theirs)
    const customerBillingAccount = billingAccounts.length > 0 ? billingAccounts[0] : null;
    
    setClaimantFormData({
      first_name: '',
      last_name: '',
      gender: '',
      date_of_birth: '',
      phone: '',
      language: '',
      customLanguage: '',
      billing_account_id: customerBillingAccount ? customerBillingAccount.id.toString() : '',
      address: '',
      address_latitude: '',
      address_longitude: ''
    });
    setClaimantSearchTerm('');
    // Reset autocomplete ref when form is reset
    if (claimantAddressAutocompleteRef.current) {
      claimantAddressAutocompleteRef.current = null;
    }
    setClaimantNameWarning(null);
    claimantNamesRef.current = { first_name: '', last_name: '' };
  };

  const handleClaimantInputChange = (field, value) => {
    setClaimantFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'first_name' || field === 'last_name') {
        claimantNamesRef.current = {
          first_name: next.first_name,
          last_name: next.last_name
        };
      }
      return next;
    });
    if (field === 'first_name' || field === 'last_name') {
      setClaimantNameWarning(null);
    }
  };

  const checkClaimantNameOnBlur = async (firstName, lastName) => {
    const fn = (firstName ?? claimantNamesRef.current.first_name ?? claimantFormData.first_name ?? '').trim();
    const ln = (lastName ?? claimantNamesRef.current.last_name ?? claimantFormData.last_name ?? '').trim();
    if (!fn || !ln) return;

    await runClaimantNameDuplicateCheck(
      makeAuthenticatedRequest,
      setDuplicateModal,
      setClaimantNameWarning,
      fn,
      ln
    );
  };

  useEffect(() => {
    if (!showClaimantModal) return;

    const fn = (claimantNamesRef.current.first_name || claimantFormData.first_name || '').trim();
    const ln = (claimantNamesRef.current.last_name || claimantFormData.last_name || '').trim();
    if (!fn || !ln) return;

    const timer = setTimeout(() => {
      checkClaimantNameOnBlur(fn, ln);
    }, 500);

    return () => clearTimeout(timer);
  }, [claimantFormData.first_name, claimantFormData.last_name, showClaimantModal]);

  const handleCreateClaimant = async (e) => {
    e.preventDefault();
    
    try {
      const claimantDup = await fetchClaimantNameDuplicate(
        makeAuthenticatedRequest,
        claimantFormData.first_name,
        claimantFormData.last_name
      );
      setClaimantNameWarning(claimantDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claimant', claimantDup))) {
        return;
      }
      // Prepare claimant data: use customLanguage if "other" is selected
      const claimantDataToSend = {
        ...claimantFormData,
        language: claimantFormData.language === 'other' ? claimantFormData.customLanguage : claimantFormData.language
      };
      // Remove customLanguage from the payload as it's not needed in the backend
      delete claimantDataToSend.customLanguage;
      
      const response = await makeAuthenticatedRequest('/claimants', {
        method: 'POST',
        body: JSON.stringify(claimantDataToSend)
      });

      if (response.success) {
        toast.success('Claimant created successfully');
        setShowClaimantModal(false);
        resetClaimantForm();
        setShowNewClaimantButton(false);
        setClaimantSearchTerm('');
        
        // Get the new claimant data
        const newClaimant = response.data;
        const newClaimantId = newClaimant?.id?.toString();
        
        if (newClaimant && newClaimantId) {
          // Add the new claimant to the list immediately
          setClaimants(prev => {
            // Check if claimant already exists to avoid duplicates
            const exists = prev.some(c => c.id === newClaimant.id);
            if (exists) {
              return prev;
            }
            return [newClaimant, ...prev];
          });
          
          // Set pending claimant ID - the useEffect will handle selection when claimant appears in list
          setPendingClaimantId(newClaimantId);
          
          // Also reload claimants in the background to ensure we have the latest data
          loadClaimants();
        }
      } else {
        toast.error(response.message || 'Failed to create claimant');
      }
    } catch (error) {
      console.error('Error creating claimant:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claimant'));
    }
  };

  const formatDateOfBirth = (dob) => {
    if (!dob) return '';
    try {
      const datePart = (typeof dob === 'string' ? dob : '').split('T')[0];
      if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
      const [year, month, day] = datePart.split('-').map((n) => parseInt(n, 10));
      return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
    } catch {
      return '';
    }
  };

  const getClaimantLabel = (claimant) => {
    const name = claimant.first_name && claimant.last_name
      ? `${claimant.first_name} ${claimant.last_name}`
      : claimant.name || 'Unnamed Claimant';
    const language = claimant.language ? ` (${claimant.language})` : '';
    const dob = formatDateOfBirth(claimant.date_of_birth);
    const dobText = dob ? ` - DOB: ${dob}` : '';
    return `${name}${dobText}${language}`;
  };

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const handleClaimFormChange = (field, value) => {
    setClaimFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'claim_number') setClaimNumberWarning(null);
  };

  const checkClaimNumberOnBlur = async () => {
    const num = (claimFormData.claim_number || '').trim();
    if (!num) { setClaimNumberWarning(null); return; }
    const claim = await fetchClaimNumberDuplicate(
      makeAuthenticatedRequest,
      num,
      editingClaim?.id
    );
    setClaimNumberWarning(claim);
    showDuplicateAlert(setDuplicateModal, 'claim', claim);
  };

  const handleSubmitNewClaim = async (e) => {
    e.preventDefault();
    
    if (!claimFormData.claim_number || !claimFormData.case_type) {
      toast.error('Claim number and case type are required');
      return;
    }
    if (!claimFormData.contact_claims_handler_id || !claimFormData.adjusters_assistant_id) {
      toast.error('Contact/claims handler and adjuster\'s assistant are required');
      return;
    }

    setIsSavingClaim(true);
    try {
      const claimDup = await fetchClaimNumberDuplicate(
        makeAuthenticatedRequest,
        claimFormData.claim_number
      );
      setClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        setIsSavingClaim(false);
        return;
      }

      const payload = {
        ...claimFormData,
        claimant_id: claimFormData.claimant_id ? parseInt(claimFormData.claimant_id, 10) : null,
        contact_claims_handler_id: claimFormData.contact_claims_handler_id || null,
        adjusters_assistant_id: claimFormData.adjusters_assistant_id || null,
        nurse_case_manager_id: claimFormData.nurse_case_manager_id || null,
        field_case_manager_id: claimFormData.field_case_manager_id || null,
        date_of_injury: claimFormData.date_of_injury || null,
        ct_start_date: claimFormData.ct_start_date || null,
        ct_end_date: claimFormData.ct_end_date || null,
        diagnosis: claimFormData.diagnosis || null,
        employer_insured: claimFormData.employer_insured || null
      };

      const response = await makeAuthenticatedRequest('/claims', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success) {
        toast.error(response.message || 'Failed to create claim');
        setIsSavingClaim(false);
        return;
      }

      toast.success('Claim created successfully');
      setShowCreateClaimModal(false);
      setIsSavingClaim(false);
      setIsEditingClaim(false);
      setEditingClaim(null);
      setClaimFormData({
        claimant_id: '',
        case_type: 'Workers Compensation',
        claim_number: '',
        date_of_injury: '',
        ct_start_date: '',
        ct_end_date: '',
        diagnosis: '',
        contact_claims_handler_id: '',
        adjusters_assistant_id: '',
        nurse_case_manager_id: '',
        field_case_manager_id: '',
        employer_insured: ''
      });
      setClaimNumberWarning(null);
      // Navigate to the newly created claim's details page
      if (response.data && response.data.id) {
        navigate(`/claims/${response.data.id}`);
      } else {
        // Fallback: reload claims list if ID is not available
        loadClaims();
      }
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claim'));
      setIsSavingClaim(false);
    }
  };

  const caseTypeOptions = [
    { value: 'Workers Compensation', label: 'Workers Compensation' },
    { value: 'Personal Injury', label: 'Personal Injury' },
    { value: 'Medical Malpractice', label: 'Medical Malpractice' },
    { value: 'Auto Accident', label: 'Auto Accident' },
    { value: 'Slip and Fall', label: 'Slip and Fall' },
    { value: 'Product Liability', label: 'Product Liability' },
    { value: 'Other', label: 'Other' }
  ];

  const handleRequestAppointment = (claim) => {
    const params = new URLSearchParams();
    if (claim.id) params.set('claimId', claim.id);
    if (claim.claimant_id) params.set('claimantId', claim.claimant_id);
    navigate(`/appointments/new?${params.toString()}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return 'N/A';
      }
      
      return localDate.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getClaimantName = (claim) => {
    if (claim.claimant_first_name && claim.claimant_last_name) {
      return `${claim.claimant_first_name} ${claim.claimant_last_name}`;
    }
    return claim.claimant_name || 'No Claimant Assigned';
  };

  const renderClaimRow = (claim) => (
    <tr
      key={claim.id}
      onClick={() => handleViewClaim(claim)}
      className="hover:bg-gray-50 cursor-pointer"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-left">
          <DocumentTextIcon className="h-5 w-5 text-indigo-600 mr-2" />
          <span className="text-sm font-medium text-gray-900">
            {claim.claim_number}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
          <span className="text-sm text-gray-900">
            {getClaimantName(claim)}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {claim.case_type || 'N/A'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-900">
            {formatDate(claim.date_of_injury)}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {claim.billing_account_name || 'N/A'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-500">
          {formatDate(claim.created_at)}
        </span>
      </td>
      <td
        className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end space-x-2">
          <button
            type="button"
            onClick={() => handleEditClaim(claim)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit Claim"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => handleRequestAppointment(claim)}
            className="text-green-600 hover:text-green-900"
            title="Request Appointment"
          >
            <CalendarIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => handleViewClaim(claim)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );

  const claimsTableHeader = (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Claim Number
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Claimant
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Case Type
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Date of Injury
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Billing Account
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Created
        </th>
        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
          Actions
        </th>
      </tr>
    </thead>
  );

  // Simple pagination component
  const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
    // Show pagination if there are more results than the page limit (30)
    if (totalCount <= 30) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * 30 + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * 30, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === pageNum
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="p-6">
      <div className="max-w-7xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Claims</h1>
              <p className="text-sm text-gray-500 mt-1">Manage and view all claims</p>
            </div>
            <button
              onClick={handleCreateClaimClick}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Claim
            </button>
          </div>

          {/* Tabs */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setActiveTab('my');
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'my'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                My Claims
              </button>
              <button
                onClick={() => {
                  setActiveTab('all');
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                All Claims
              </button>
            </div>
          </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by claim number..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading claims...
          </div>
        ) : claims.length === 0 && !(activeTab === 'my' && allClaimsSearchResults.length > 0) ? (
          <div className="p-8 text-center text-gray-500">
            <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium">No claims found</p>
            <p className="text-sm mt-2">
              {debouncedSearchTerm 
                ? `No claims match "${debouncedSearchTerm}" in My Claims`
                : 'Get started by creating a new claim'}
            </p>
            {!debouncedSearchTerm && (
                <button
                  onClick={handleCreateClaimClick}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Your First Claim
              </button>
            )}
          </div>
        ) : claims.length === 0 && activeTab === 'my' && allClaimsSearchResults.length > 0 ? (
          <div className="p-6 text-center text-gray-500 border-b border-gray-100">
            <p className="text-sm">
              No matches for &ldquo;{debouncedSearchTerm}&rdquo; in <strong>My Claims</strong>.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                {claimsTableHeader}
                <tbody className="bg-white divide-y divide-gray-200">
                  {claims.map((claim) => renderClaimRow(claim))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {!loading && totalCount > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / 30)}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* All Claims matches when searching from My Claims tab */}
      {!loading && activeTab === 'my' && debouncedSearchTerm && allClaimsSearchResults.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
            <p className="text-sm font-medium text-amber-900">
              Found in All Claims
            </p>
            <p className="text-xs text-amber-800 mt-1">
              These claims match your search on your billing account but you are not the assigned
              contact handler or assistant. You can view them or assign yourself as the adjuster.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {claimsTableHeader}
              <tbody className="bg-white divide-y divide-gray-200">
                {allClaimsSearchResults.map((claim) => renderClaimRow(claim))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>

    {showCreateClaimModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
          <div className="flex items-center justify-between border-b pb-4 mb-6">
              <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 mr-3 text-indigo-600" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditingClaim ? 'Edit Claim' : 'Create New Claim'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditingClaim ? 'Update claim information' : 'Add a new claim to the system'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowCreateClaimModal(false);
                setShowNewClaimantButton(false);
                setIsEditingClaim(false);
                setEditingClaim(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form className="space-y-6" onSubmit={isEditingClaim ? handleUpdateClaim : handleSubmitNewClaim}>
            {/* Claimant Selection - Optional, can be assigned after creation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Claimant (Optional - can be assigned after creation)
              </label>
              <SearchableSelect
                key={`claimant-select-${claimants.length}-${claimFormData.claimant_id}`}
                options={claimants.map(c => ({
                  value: c.id.toString(),
                  label: getClaimantLabel(c)
                }))}
                value={claimFormData.claimant_id}
                onChange={(value) => handleClaimFormChange('claimant_id', value)}
                placeholder="Select a claimant (optional)..."
                searchPlaceholder="Search claimants..."
                onSearchChange={(searchTerm, hasResults) => {
                  // Show button only when searching and no results found
                  setShowNewClaimantButton(searchTerm.length > 0 && !hasResults);
                  // Store the search term for auto-filling the name
                  setClaimantSearchTerm(searchTerm);
                }}
                noResultsAction={showNewClaimantButton ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Parse the search term to extract first and last name
                      const nameParts = claimantSearchTerm.trim().split(/\s+/);
                      const firstName = nameParts[0] || '';
                      const lastName = nameParts.slice(1).join(' ') || '';
                      
                      // Get the customer's billing account (first one from the list, which should be theirs)
                      const customerBillingAccount = billingAccounts.length > 0 ? billingAccounts[0] : null;
                      
                      // Set the form data with auto-filled name and billing account
                      setClaimantFormData({
                        first_name: firstName,
                        last_name: lastName,
                        gender: '',
                        date_of_birth: '',
                        phone: '',
                        language: '',
                        billing_account_id: customerBillingAccount ? customerBillingAccount.id.toString() : '',
                        address: ''
                      });
                      
                      setShowClaimantModal(true);
                    }}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center whitespace-nowrap"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    New Claimant
                  </button>
                ) : null}
              />
              <p className="text-xs text-gray-500 mt-1">You can assign a claimant after creating the claim</p>
            </div>

            {/* Claim Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Claim Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={claimFormData.claim_number}
                onChange={(e) => handleClaimFormChange('claim_number', e.target.value)}
                onBlur={checkClaimNumberOnBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Case Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Case Type <span className="text-red-500">*</span>
              </label>
              <select
                value={claimFormData.case_type}
                onChange={(e) => handleClaimFormChange('case_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {caseTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Date of Injury */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Injury
              </label>
              <input
                type="date"
                value={claimFormData.date_of_injury}
                onChange={(e) => handleClaimFormChange('date_of_injury', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* CT Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CT Start Date
              </label>
              <input
                type="date"
                value={claimFormData.ct_start_date}
                onChange={(e) => handleClaimFormChange('ct_start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* CT End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CT End Date
              </label>
              <input
                type="date"
                value={claimFormData.ct_end_date}
                onChange={(e) => handleClaimFormChange('ct_end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Diagnosis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Diagnosis
              </label>
              <textarea
                value={claimFormData.diagnosis}
                onChange={(e) => handleClaimFormChange('diagnosis', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Employer/Insured */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employer/Insured
              </label>
              <input
                type="text"
                value={claimFormData.employer_insured}
                onChange={(e) => handleClaimFormChange('employer_insured', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter employer or insured party name (optional)"
              />
            </div>

            {/* Contact Claims Handler */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact/Claims Handler <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={customers.map(c => ({
                  value: c.id.toString(),
                  label: c.name || 'Unnamed Customer'
                }))}
                value={claimFormData.contact_claims_handler_id}
                onChange={(value) => handleClaimFormChange('contact_claims_handler_id', value)}
                placeholder="Select contact/claims handler..."
                searchPlaceholder="Search customers..."
              />
            </div>

            {/* Adjuster's Assistant */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adjuster's Assistant <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={customers.map(c => ({
                  value: c.id.toString(),
                  label: c.name || 'Unnamed Customer'
                }))}
                value={claimFormData.adjusters_assistant_id}
                onChange={(value) => handleClaimFormChange('adjusters_assistant_id', value)}
                placeholder="Select adjuster's assistant..."
                searchPlaceholder="Search customers..."
              />
            </div>

            {/* Nurse Case Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nurse Case Manager
              </label>
              <SearchableSelect
                options={customers.map(c => ({
                  value: c.id.toString(),
                  label: c.name || 'Unnamed Customer'
                }))}
                value={claimFormData.nurse_case_manager_id}
                onChange={(value) => handleClaimFormChange('nurse_case_manager_id', value)}
                placeholder="Select nurse case manager..."
                searchPlaceholder="Search customers..."
              />
            </div>

            {/* Field Case Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field Case Manager
              </label>
              <SearchableSelect
                options={customers.map(c => ({
                  value: c.id.toString(),
                  label: c.name || 'Unnamed Customer'
                }))}
                value={claimFormData.field_case_manager_id}
                onChange={(value) => handleClaimFormChange('field_case_manager_id', value)}
                placeholder="Select field case manager..."
                searchPlaceholder="Search customers..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowCreateClaimModal(false);
                  setShowNewClaimantButton(false);
                  setIsEditingClaim(false);
                  setEditingClaim(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isSavingClaim}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSavingClaim}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingClaim 
                  ? (isEditingClaim ? 'Updating...' : 'Creating...') 
                  : (isEditingClaim ? 'Update Claim' : 'Create Claim')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Create Claimant Modal */}
    {showClaimantModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <UserIcon className="h-6 w-6 mr-2 text-blue-600" />
                Create New Claimant
              </h2>
              <button
                onClick={() => {
                  setShowClaimantModal(false);
                  resetClaimantForm();
                  setShowNewClaimantButton(false);
                  setClaimantSearchTerm('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateClaimant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={claimantFormData.first_name}
                    onChange={(e) => handleClaimantInputChange('first_name', e.target.value)}
                    onBlur={(e) => {
                      const firstName = capitalizeName(e.target.value);
                      claimantNamesRef.current.first_name = firstName;
                      handleClaimantInputChange('first_name', firstName);
                      checkClaimantNameOnBlur(firstName, claimantNamesRef.current.last_name);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={claimantFormData.last_name}
                    onChange={(e) => handleClaimantInputChange('last_name', e.target.value)}
                    onBlur={(e) => {
                      const lastName = capitalizeName(e.target.value);
                      claimantNamesRef.current.last_name = lastName;
                      handleClaimantInputChange('last_name', lastName);
                      checkClaimantNameOnBlur(claimantNamesRef.current.first_name, lastName);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    value={claimantFormData.gender}
                    onChange={(e) => handleClaimantInputChange('gender', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {genderOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={claimantFormData.date_of_birth}
                    onChange={(e) => handleClaimantInputChange('date_of_birth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={claimantFormData.phone}
                    onChange={(e) => handleClaimantInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Language
                  </label>
                  <SearchableSelect
                    options={[
                      ...languages.filter(l => (l.name || l).toLowerCase() !== 'agency').map(l => ({
                        value: l.name || l,
                        label: l.name || l
                      })),
                      { value: 'other', label: 'Other' }
                    ]}
                    value={claimantFormData.language}
                    onChange={(value) => {
                      handleClaimantInputChange('language', value);
                      // Clear custom language if not "other"
                      if (value !== 'other') {
                        handleClaimantInputChange('customLanguage', '');
                      }
                    }}
                    placeholder="Select language..."
                    searchPlaceholder="Search languages..."
                  />
                  {claimantFormData.language === 'other' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={claimantFormData.customLanguage}
                        onChange={(e) => handleClaimantInputChange('customLanguage', e.target.value)}
                        placeholder="Please specify the language"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Account
                </label>
                <SearchableSelect
                  options={billingAccounts.map(ba => ({
                    value: ba.id.toString(),
                    label: ba.name || 'Unnamed Account'
                  }))}
                  value={claimantFormData.billing_account_id}
                  onChange={(value) => handleClaimantInputChange('billing_account_id', value)}
                  placeholder="Select billing account..."
                  searchPlaceholder="Search billing accounts..."
                  disabled={true}
                />
                <p className="text-xs text-gray-500 mt-1">Billing account is automatically set to your account</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={claimantFormData.address}
                  onChange={(e) => handleClaimantInputChange('address', e.target.value)}
                  placeholder={mapsInitialized ? "Start typing address for autocomplete..." : "Enter address manually"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ref={(input) => {
                    if (input && mapsInitialized && !claimantAddressAutocompleteRef.current) {
                      setupClaimantAddressAutocomplete(input);
                    }
                  }}
                />
                {(claimantFormData.address_latitude && claimantFormData.address_longitude) && (
                  <div className="mt-2 text-xs text-gray-500">
                    Coordinates: {claimantFormData.address_latitude}, {claimantFormData.address_longitude}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowClaimantModal(false);
                    resetClaimantForm();
                    setShowNewClaimantButton(false);
                    setClaimantSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Claimant
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}
    {/* Adjuster Confirmation Modal */}
    <AdjusterConfirmModal
      isOpen={!!pendingClaim}
      claim={pendingClaim}
      onSave={async (payload) => {
        await saveAssignment(payload);
        loadClaims();
      }}
      onSkip={skipPrompt}
      saving={savingRole}
    />

    <DuplicateWarningModal
      isOpen={!!duplicateModal}
      type={duplicateModal?.type}
      data={duplicateModal?.data}
      mode={duplicateModal?.mode}
      onClose={() => {
        duplicateModal?.onResolve?.(false);
        setDuplicateModal(null);
      }}
      onConfirm={() => {
        duplicateModal?.onResolve?.(true);
        setDuplicateModal(null);
      }}
      onViewClaimant={(id) => navigate(`/claimants?claimantId=${id}`)}
      onViewClaim={viewClaim}
    />
    </>
  );
};

export default Claims;

